import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, PauseIcon, XIcon, Volume2Icon, VolumeXIcon, MaximizeIcon, SkipForwardIcon } from 'lucide-react';
interface DemoScene {
  id: number;
  title: string;
  description: string;
  duration: number;
  animation: React.ReactNode;
}
interface VeriBotDemoProps {
  isOpen: boolean;
  onClose: () => void;
}
const VeriBotDemo: React.FC<VeriBotDemoProps> = ({
  isOpen,
  onClose
}) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const scenes: DemoScene[] = [{
    id: 1,
    title: 'Meet VeriBot',
    description: 'Your 24/7 AI Property Assistant',
    duration: 3000,
    animation: <div className="flex items-center justify-center h-full" data-id="element-521">
          <motion.div initial={{
        scale: 0,
        rotate: -180
      }} animate={{
        scale: 1,
        rotate: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="relative" data-id="element-522">
            <div className="w-32 h-32 bg-gradient-to-br from-[#FF4E00] via-[#FF6B2C] to-[#FF8C4D] rounded-3xl flex items-center justify-center shadow-2xl" data-id="element-523">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-524">
                <motion.path initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1,
              delay: 0.5
            }} d="M12 2L2 7L12 12L22 7L12 2Z" fill="white" fillOpacity="0.9" data-id="element-525" />
                <motion.path initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1,
              delay: 0.7
            }} d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-526" />
                <motion.path initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1,
              delay: 0.9
            }} d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-527" />
                <motion.circle initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 1.2
            }} cx="12" cy="7" r="2" fill="#1A1F47" data-id="element-528" />
              </svg>
            </div>
            <motion.div animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 bg-[#FF4E00] rounded-3xl -z-10" data-id="element-529" />
          </motion.div>
        </div>
  }, {
    id: 2,
    title: 'AI Property Search',
    description: 'Find properties with intelligent recommendations',
    duration: 4000,
    animation: <div className="flex items-center justify-center h-full p-8" data-id="element-530">
          <div className="w-full max-w-md space-y-4" data-id="element-531">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-xl p-4" data-id="element-532">
              <div className="flex items-center space-x-3 mb-3" data-id="element-533">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF4E00] to-[#FF6B2C] rounded-xl flex items-center justify-center" data-id="element-534">
                  <span className="text-white text-lg" data-id="element-535">👤</span>
                </div>
                <div className="flex-1 bg-gray-100 rounded-xl px-4 py-2" data-id="element-536">
                  <motion.p initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.3
              }} className="text-sm text-gray-800" data-id="element-537">
                    Show me properties in Lekki under ₦100M
                  </motion.p>
                </div>
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8
          }} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-3 border border-gray-200" data-id="element-538">
                <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 1
            }} src="https://images.unsplash.com/photo-1613545325278-f24b0cae1224?ixlib=rb-4.0.3&w=400&q=80" alt="Property" className="w-full h-32 object-cover rounded-lg mb-2" data-id="element-539" />
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.2
            }} data-id="element-540">
                  <div className="flex items-center justify-between mb-1" data-id="element-541">
                    <h4 className="font-semibold text-sm" data-id="element-542">
                      Luxury 3BR Apartment
                    </h4>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded" data-id="element-543">
                      8.7/10
                    </span>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-544">Lekki Phase 1, Lagos</p>
                  <p className="text-[#FF4E00] font-bold text-sm mt-1" data-id="element-545">
                    ₦75,000,000
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
  }, {
    id: 3,
    title: 'Live Document Tracking',
    description: 'Monitor verification status in real-time',
    duration: 4000,
    animation: <div className="flex items-center justify-center h-full p-8" data-id="element-546">
          <div className="w-full max-w-md" data-id="element-547">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-xl p-6" data-id="element-548">
              <h3 className="font-bold text-lg mb-4 text-gray-800" data-id="element-549">
                Document Status
              </h3>
              <div className="space-y-3" data-id="element-550">
                {[{
              name: 'Certificate of Occupancy',
              progress: 100,
              delay: 0
            }, {
              name: 'Survey Plan',
              progress: 100,
              delay: 0.3
            }, {
              name: "Governor's Consent",
              progress: 75,
              delay: 0.6
            }, {
              name: 'Tax Clearance',
              progress: 0,
              delay: 0.9
            }].map((doc, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: doc.delay
            }} className="bg-gray-50 rounded-lg p-3" data-id="element-551">
                    <div className="flex items-center justify-between mb-2" data-id="element-552">
                      <span className="text-sm font-medium text-gray-800" data-id="element-553">
                        {doc.name}
                      </span>
                      <span className="text-xs text-gray-600" data-id="element-554">
                        {doc.progress}%
                      </span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-2" data-id="element-555">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${doc.progress}%`
                }} transition={{
                  duration: 1,
                  delay: doc.delay + 0.3
                }} className={`h-2 rounded-full ${doc.progress === 100 ? 'bg-green-500' : doc.progress > 0 ? 'bg-blue-500' : 'bg-gray-300'}`} data-id="element-556" />
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
  }, {
    id: 4,
    title: 'VeriScore Analysis',
    description: 'AI-powered investment insights',
    duration: 4000,
    animation: <div className="flex items-center justify-center h-full p-8" data-id="element-557">
          <div className="w-full max-w-md" data-id="element-558">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-6 text-white" data-id="element-559">
              <div className="flex items-center justify-between mb-6" data-id="element-560">
                <div data-id="element-561">
                  <h3 className="text-2xl font-bold mb-1" data-id="element-562">
                    Lekki Phase 1 Property
                  </h3>
                  <p className="text-white/80 text-sm" data-id="element-563">Investment Analysis</p>
                </div>
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              delay: 0.3
            }} className="text-right" data-id="element-564">
                  <div className="text-5xl font-bold" data-id="element-565">8.7</div>
                  <div className="text-sm text-white/80" data-id="element-566">/ 10</div>
                </motion.div>
              </div>

              <div className="grid grid-cols-2 gap-3" data-id="element-567">
                {[{
              label: 'Rental Yield',
              value: '6.2%',
              delay: 0.5
            }, {
              label: 'Appreciation',
              value: '+12%',
              delay: 0.6
            }, {
              label: 'Infrastructure',
              value: '9/10',
              delay: 0.7
            }, {
              label: 'Demand',
              value: 'High',
              delay: 0.8
            }].map((metric, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: metric.delay
            }} className="bg-white/20 backdrop-blur-sm rounded-xl p-3" data-id="element-568">
                    <p className="text-xs text-white/80 mb-1" data-id="element-569">{metric.label}</p>
                    <p className="text-xl font-bold" data-id="element-570">{metric.value}</p>
                  </motion.div>)}
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1
          }} className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-3" data-id="element-571">
                <p className="text-sm" data-id="element-572">
                  ✨ <strong data-id="element-573">Strong buy opportunity</strong> - High growth
                  potential with excellent infrastructure development
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
  }, {
    id: 5,
    title: 'Contact Management',
    description: 'Connect with agents instantly',
    duration: 3500,
    animation: <div className="flex items-center justify-center h-full p-8" data-id="element-574">
          <div className="w-full max-w-md" data-id="element-575">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-xl p-6" data-id="element-576">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            delay: 0.3
          }} className="flex items-center space-x-4 mb-4" data-id="element-577">
                <div className="relative" data-id="element-578">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&w=200&q=80" alt="Agent" className="w-16 h-16 rounded-full object-cover" data-id="element-579" />
                  <motion.div animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2
              }} className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" data-id="element-580" />
                </div>
                <div data-id="element-581">
                  <h4 className="font-bold text-lg text-gray-800" data-id="element-582">
                    Victoria Adeyemi
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-583">Senior Property Agent</p>
                  <p className="text-xs text-green-600 font-medium" data-id="element-584">
                    ● Available Now
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="space-y-2" data-id="element-585">
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2" data-id="element-586">
                  <span data-id="element-587">📞</span>
                  <span data-id="element-588">Call Now</span>
                </motion.button>
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-gray-100 text-gray-800 py-3 rounded-xl font-semibold flex items-center justify-center space-x-2" data-id="element-589">
                  <span data-id="element-590">✉️</span>
                  <span data-id="element-591">Send Email</span>
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </div>
  }, {
    id: 6,
    title: 'Always Available',
    description: '24/7 intelligent assistance for your property journey',
    duration: 3000,
    animation: <div className="flex items-center justify-center h-full" data-id="element-592">
          <div className="text-center" data-id="element-593">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200
        }} className="mb-6" data-id="element-594">
              <div className="w-24 h-24 bg-gradient-to-br from-[#FF4E00] to-[#FF6B2C] rounded-full flex items-center justify-center mx-auto shadow-2xl" data-id="element-595">
                <span className="text-5xl" data-id="element-596">🤖</span>
              </div>
            </motion.div>
            <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-3xl font-bold text-gray-800 mb-3" data-id="element-597">
              VeriBot is Ready
            </motion.h2>
            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="text-xl text-gray-600 mb-6" data-id="element-598">
              Your AI assistant is available 24/7
            </motion.p>
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.7
        }} data-id="element-599">
              <button className="px-8 py-4 bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition" data-id="element-600">
                Start Chatting Now
              </button>
            </motion.div>
          </div>
        </div>
  }];
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentScene < scenes.length - 1) {
            setCurrentScene(prev => prev + 1);
            return 0;
          } else {
            setIsPlaying(false);
            return 100;
          }
        }
        return prev + 100 / (scenes[currentScene].duration / 100);
      });
    }, 100);
    return () => clearInterval(timer);
  }, [isPlaying, currentScene, scenes]);
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  const handleSkip = () => {
    if (currentScene < scenes.length - 1) {
      setCurrentScene(prev => prev + 1);
      setProgress(0);
      setIsPlaying(true);
    }
  };
  const handleRestart = () => {
    setCurrentScene(0);
    setProgress(0);
    setIsPlaying(true);
  };
  return <AnimatePresence data-id="element-601">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-id="element-602">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl shadow-2xl overflow-hidden" data-id="element-603">
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-white/10 hover:bg-white/20 rounded-full transition" data-id="element-604">
              <XIcon className="w-6 h-6 text-white" data-id="element-605" />
            </button>

            {/* Video Content */}
            <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 relative" data-id="element-606">
              <AnimatePresence mode="wait" data-id="element-607">
                <motion.div key={currentScene} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="absolute inset-0" data-id="element-608">
                  {scenes[currentScene].animation}
                </motion.div>
              </AnimatePresence>

              {/* Scene Info Overlay */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6" data-id="element-609">
                <h3 className="text-2xl font-bold text-white mb-1" data-id="element-610">
                  {scenes[currentScene].title}
                </h3>
                <p className="text-white/80" data-id="element-611">
                  {scenes[currentScene].description}
                </p>
              </motion.div>
            </div>

            {/* Controls */}
            <div className="bg-gray-900 p-4" data-id="element-612">
              {/* Progress Bar */}
              <div className="mb-4" data-id="element-613">
                <div className="bg-gray-700 rounded-full h-1.5 overflow-hidden" data-id="element-614">
                  <motion.div className="bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] h-full" style={{
                width: `${progress}%`
              }} data-id="element-615" />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-400" data-id="element-616">
                  <span data-id="element-617">
                    Scene {currentScene + 1} of {scenes.length}
                  </span>
                  <span data-id="element-618">
                    {Math.floor(progress / 100 * (scenes[currentScene].duration / 1000))}
                    s / {scenes[currentScene].duration / 1000}s
                  </span>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between" data-id="element-619">
                <div className="flex items-center space-x-3" data-id="element-620">
                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} onClick={handlePlayPause} className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition" data-id="element-621">
                    {isPlaying ? <PauseIcon className="w-5 h-5 text-white" data-id="element-622" /> : <PlayIcon className="w-5 h-5 text-white" data-id="element-623" />}
                  </motion.button>

                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} onClick={handleSkip} disabled={currentScene === scenes.length - 1} className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition disabled:opacity-50" data-id="element-624">
                    <SkipForwardIcon className="w-5 h-5 text-white" data-id="element-625" />
                  </motion.button>

                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} onClick={() => setIsMuted(!isMuted)} className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition" data-id="element-626">
                    {isMuted ? <VolumeXIcon className="w-5 h-5 text-white" data-id="element-627" /> : <Volume2Icon className="w-5 h-5 text-white" data-id="element-628" />}
                  </motion.button>
                </div>

                <div className="flex items-center space-x-3" data-id="element-629">
                  {currentScene === scenes.length - 1 && progress === 100 && <motion.button initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleRestart} className="px-6 py-2 bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] text-white font-semibold rounded-lg" data-id="element-630">
                      Watch Again
                    </motion.button>}

                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition" data-id="element-631">
                    <MaximizeIcon className="w-5 h-5 text-white" data-id="element-632" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};
export default VeriBotDemo;